import { PATCH_MAX_INSTRUMENT_HISTORY_YEARS } from "@repo/shared";
import type { LocalizedString, ProductSustainability, SfdrScoreValue } from "@repo/shared/types";
import {
  DEFAULT_SUSTAINABILITY_EXCLUSION_MODE,
  resolveProductPreferenceFromInstrument,
  SFDR_SCORE_VALUES,
} from "@repo/shared/types";
import { ArrowDown, ArrowUp, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogCancel,
  DialogDescription,
  DialogScrollContent,
  DialogScrollFooter,
  DialogScrollHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { InlineSvg } from "@/components/ui/inline-svg";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LocalizedInput } from "@/components/ui/localized-input";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ApiError } from "@/lib/api/_client";
import { isWithinLastNYears } from "@/lib/date-utils";
import {
  useClientPreferences,
  useCreateProduct,
  useInstrumentMetadata,
  usePlatforms,
  usePortfolioCollection,
  useProductCostClasses,
  useSuitabilityOptions,
  useSustainabilityConfig,
  useUpdateProduct,
  useWrapperTypes,
} from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { computeApiCostValues } from "@/lib/utils/compute-api-cost-values";
import { getIdentityFieldsToClear } from "@/lib/utils/identity-fields-to-clear";
import { InstrumentSearchDialog, type InstrumentSelectResult } from "./instrument-search-dialog";
import { ProductCostsDisplay } from "./product-costs-display";
import { T } from "./product-dialog.i18n";
import { ProductDocumentsTab } from "./product-documents-tab";

type ProductDialogProps = {
  open: boolean;
  portfolioCollectionId: string;
  product: any | null;
  costTypes: any[];
  assetClasses: any[];
  onClose: () => void;
  defaultTab?: string;
};

export function ProductDialog({
  open,
  portfolioCollectionId,
  product,
  costTypes,
  assetClasses,
  onClose,
  defaultTab = "general",
}: ProductDialogProps) {
  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const { formatDate } = useRegionalFormat();
  const t = useT(T);

  const editingProductName = product ? localize(product.nameI18n) : "";

  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const isPending = createProduct.isPending || updateProduct.isPending;

  const { productCostClasses } = useProductCostClasses(portfolioCollectionId);
  const { platforms } = usePlatforms(portfolioCollectionId);
  // Wrapper types are gated on the collection setting — a disabled collection
  // never shows the picker and never sends wrapperTypeIds (undefined = untouched).
  const { collection: dialogCollection } = usePortfolioCollection(portfolioCollectionId);
  const wrapperTypesFeatureEnabled = dialogCollection?.wrapperTypesEnabled ?? false;
  // QL-383 — the product type field is hidden while the collection has product
  // types disabled (the options payload is empty then, so nothing gets seeded).
  const productTypesFeatureEnabled = dialogCollection?.productTypesEnabled ?? false;
  // QP-164 — expected return/volatility drive calculations only in Products
  // mode; in Asset Classes mode the asset class's own fields are authoritative.
  const isProductsMode = dialogCollection?.calculationsMode === "products";
  const { wrapperTypes } = useWrapperTypes(
    wrapperTypesFeatureEnabled ? portfolioCollectionId : null,
  );
  const activeWrapperTypes = wrapperTypes.filter((w) => w.isActive);
  // QP-179 — sustainability criteria of the collection (null = disabled)
  const { config: sustainabilityConfig } = useSustainabilityConfig(portfolioCollectionId);
  // QP-224 — client preference definitions of the collection (empty = feature unused)
  const { clientPreferences } = useClientPreferences(portfolioCollectionId);
  const activeCostClasses = productCostClasses.filter((c) => c.isActive);

  const { suitabilityOptions, isFetching: suitabilityOptionsFetching } = useSuitabilityOptions(
    open ? portfolioCollectionId : null,
    open ? (product?.id ?? null) : null,
  );

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [error, setError] = useState<string | null>(null);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [patchInstrumentDialogOpen, setPatchInstrumentDialogOpen] = useState(false);
  /**
   * QL-271 — the server's reason for refusing the patch on the last save,
   * pinned under the patch block so the advisor is not left matching the banner
   * against the form. Cleared whenever the patch or the instrument changes.
   */
  const [patchError, setPatchError] = useState<string | null>(null);
  const [linkedInstrument, setLinkedInstrument] = useState<{
    ticker: string;
    dataset: string;
    name: string;
    patchInstrument?: {
      ticker?: string;
      dataset?: string;
      name?: string;
    };
  } | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    nameI18n: {} as LocalizedString,
    isin: "",
    description: "",
    descriptionI18n: {} as LocalizedString,
    ticker: "",
    dataset: "",
    patchTicker: "",
    patchDataset: "",
    productTypeId: "",
    productCostClassId: "",
    minTimeHorizonId: "",
    minRiskLevelId: "",
    minRiskScore: "",
    minBuyAmount: "",
    expectedReturn: "",
    expectedVolatility: "",
    orderAmountType: "amount" as "amount" | "units",
    firstDate: "",
    lastDate: "",
    instrumentCurrency: "",
    patchFirstDate: "",
    patchLastDate: "",
    patchCurrency: "",
  });

  // QP-226 — explicit platform membership (empty = available under no platform)
  const [platformIds, setPlatformIds] = useState<string[]>([]);

  // Wrapper type ids, preference-ordered (index 0 = preferred)
  const [wrapperTypeIds, setWrapperTypeIds] = useState<string[]>([]);

  // QP-179 — the product's sustainability values per configured criterion
  const [sustainability, setSustainability] = useState<ProductSustainability>({});

  // QP-224 — the product's per-preference values (missing key = false)
  const [clientPreferenceValues, setClientPreferenceValues] = useState<Record<string, boolean>>({});

  // Cost values state: costTypeId -> { calculatedValue, overriddenValue }
  const [costValues, setCostValues] = useState<
    Record<string, { calculatedValue: number; overriddenValue: number | null }>
  >({});

  // QL-10 — selectable minimum risk scores: the collection risk model's matrix
  // values, plus the currently stored one when it falls outside them (an
  // AI-set in-between score, or a matrix edited after the product was saved),
  // so such a value stays visible and clearable.
  const minRiskScoreOptions = useMemo(() => {
    const current = Number(formData.minRiskScore);
    const scores = new Set(suitabilityOptions.matrixScores);
    if (formData.minRiskScore.trim() !== "" && Number.isFinite(current)) scores.add(current);
    return [...scores].sort((a, b) => a - b);
  }, [suitabilityOptions.matrixScores, formData.minRiskScore]);

  // One-shot guard for the auto-assign-on-create effect.
  const autoAssignDoneRef = useRef(false);

  // QL-11 — its own one-shot guard, deliberately not shared with the cost
  // auto-assign above: whichever effect fired first would otherwise disarm the
  // other.
  const defaultProductTypeSeededRef = useRef(false);

  // QL-174 — the name/ISIN the last instrument link wrote into the form;
  // removing the instrument clears a field only while it still equals this.
  const autoFilledIdentityRef = useRef<{ name: string; isin: string } | null>(null);

  // Asset allocation state
  const [allocationMode, setAllocationMode] = useState<"none" | "single" | "multiple">("none");
  const [selectedAssetClassId, setSelectedAssetClassId] = useState<string>("");
  const [allocationWeights, setAllocationWeights] = useState<Record<string, string>>({});

  // Once a product has allocations, they cannot be removed — only replaced with another valid set.
  const productHasExistingAllocations = (product?.assetAllocations?.length ?? 0) > 0;

  const { metadata: mainMetadata, isLoading: mainMetadataLoading } = useInstrumentMetadata(
    open && formData.ticker ? formData.ticker : null,
    open && formData.dataset ? formData.dataset : null,
  );
  const { metadata: patchMetadata } = useInstrumentMetadata(
    open && formData.patchTicker ? formData.patchTicker : null,
    open && formData.patchDataset ? formData.patchDataset : null,
  );

  // Instrument last price (from flattened instrument metadata) — gates the "units"
  // order type and is shown next to the selector.
  const mm = mainMetadata as Record<string, unknown> | null;
  // QP-179 — the linked Morningstar instrument's SFDR type + exclusion booleans,
  // used to LIVE-RESOLVE the product's SFDR + mapped product preferences (with optional override).
  const instrumentSfdr: SfdrScoreValue | null =
    mm && SFDR_SCORE_VALUES.includes(mm.eusfdrType as SfdrScoreValue)
      ? (mm.eusfdrType as SfdrScoreValue)
      : null;
  // getInstrumentMetadata keeps the two ESG sector maps as intact named objects
  // (they survive the flatten). A mapped product preference's value is live-resolved
  // from them per the preference's exclusionMode via the shared resolver.
  const instrumentEsgMaps = {
    esgExclusions: (mm?.esgExclusions as Record<string, boolean> | undefined) ?? {},
    esgInvolvement: (mm?.esgInvolvement as Record<string, boolean> | undefined) ?? {},
  };
  const orderUnitsLastPrice =
    mm && typeof mm.lastPrice === "number"
      ? {
          value: mm.lastPrice as number,
          currency: (mm.currency as string | undefined) ?? null,
          date: (mm.lastPriceDate as string | undefined) ?? null,
        }
      : null;

  useEffect(() => {
    if (!mainMetadata) return;
    setFormData((prev) => ({
      ...prev,
      firstDate: mainMetadata.firstdate || "",
      lastDate: mainMetadata.lastdate || "",
      instrumentCurrency: mainMetadata.currency || "",
    }));
    setLinkedInstrument((prev) =>
      prev ? { ...prev, name: mainMetadata.name || prev.name } : prev,
    );
  }, [mainMetadata]);

  // QL-271 — the instrument-metadata endpoint returns flat `firstdate` /
  // `lastdate` (the same fields the main-instrument effect above reads), never a
  // `dates` array. Reading the latter wiped both patch dates on every fetch.
  useEffect(() => {
    if (!patchMetadata) return;
    setFormData((prev) => ({
      ...prev,
      patchFirstDate: patchMetadata.firstdate || "",
      patchLastDate: patchMetadata.lastdate || "",
      patchCurrency: patchMetadata.currency || "",
    }));
    setLinkedInstrument((prev) =>
      prev?.patchInstrument
        ? {
            ...prev,
            patchInstrument: {
              ...prev.patchInstrument,
              name: patchMetadata.name || prev.patchInstrument.name,
            },
          }
        : prev,
    );
  }, [patchMetadata]);

  useEffect(() => {
    if (product) {
      const productName = localize(product.nameI18n);
      const productDescription = product.descriptionI18n ? localize(product.descriptionI18n) : "";
      setFormData({
        name: productName,
        nameI18n: product.nameI18n || {},
        isin: product.isin || "",
        description: productDescription,
        descriptionI18n: product.descriptionI18n || {},
        ticker: product.ticker || "",
        dataset: product.dataset || "",
        patchTicker: product.patchTicker || "",
        patchDataset: product.patchDataset || "",
        productTypeId: product.productTypeId || "",
        productCostClassId: product.productCostClassId || "",
        minTimeHorizonId: product.minTimeHorizonId || "",
        minRiskLevelId: product.minRiskLevelId || "",
        minRiskScore: product.minRiskScore != null ? String(product.minRiskScore) : "",
        minBuyAmount: product.minBuyAmount != null ? String(product.minBuyAmount) : "",
        expectedReturn: product.expectedReturn != null ? String(product.expectedReturn) : "",
        expectedVolatility:
          product.expectedVolatility != null ? String(product.expectedVolatility) : "",
        orderAmountType: product.orderAmountType === "units" ? "units" : "amount",
        firstDate: "",
        lastDate: "",
        instrumentCurrency: "",
        patchFirstDate: "",
        patchLastDate: "",
        patchCurrency: "",
      });
      // QP-195 — platform availability
      setPlatformIds(product.platformIds ?? []);
      setWrapperTypeIds(product.wrapperTypeIds ?? []);
      // QP-179 — sustainability values
      setSustainability(product.sustainability ?? {});
      // QP-224 — client preference values
      setClientPreferenceValues(product.clientPreferences ?? {});
      // Set linked instrument if product has ticker and dataset
      if (product.ticker && product.dataset) {
        const patchInstrumentData =
          product.patchTicker && product.patchDataset
            ? {
                ticker: product.patchTicker,
                dataset: product.patchDataset,
                name: undefined as string | undefined,
              }
            : undefined;

        setLinkedInstrument({
          ticker: product.ticker,
          dataset: product.dataset,
          name: productName, // Temporarly setting the name of the main product as the linked instrument name itself.
          patchInstrument: patchInstrumentData,
        });
      } else {
        setLinkedInstrument(null);
      }
      // Initialize cost values from product
      const initialCostValues: Record<
        string,
        { calculatedValue: number; overriddenValue: number | null }
      > = {};
      for (const cv of product.costValues || []) {
        initialCostValues[cv.costTypeId] = {
          calculatedValue: cv.calculatedValue,
          overriddenValue: cv.overriddenValue,
        };
      }
      setCostValues(initialCostValues);

      // Initialize asset allocations from product
      const allocations = product.assetAllocations || [];
      if (allocations.length === 0) {
        setAllocationMode("none");
        setSelectedAssetClassId("");
        setAllocationWeights({});
      } else if (allocations.length === 1 && allocations[0].weight === 100) {
        setAllocationMode("single");
        setSelectedAssetClassId(allocations[0].assetClassId);
        setAllocationWeights({});
      } else {
        setAllocationMode("multiple");
        setSelectedAssetClassId("");
        const weights: Record<string, string> = {};
        for (const alloc of allocations) {
          weights[alloc.assetClassId] = String(alloc.weight);
        }
        setAllocationWeights(weights);
      }
    } else {
      setFormData({
        name: "",
        nameI18n: {},
        isin: "",
        description: "",
        descriptionI18n: {},
        ticker: "",
        dataset: "",
        patchTicker: "",
        patchDataset: "",
        productTypeId: "",
        productCostClassId: "",
        minTimeHorizonId: "",
        minRiskLevelId: "",
        minRiskScore: "",
        minBuyAmount: "",
        expectedReturn: "",
        expectedVolatility: "",
        orderAmountType: "amount",
        firstDate: "",
        lastDate: "",
        instrumentCurrency: "",
        patchFirstDate: "",
        patchLastDate: "",
        patchCurrency: "",
      });
      setLinkedInstrument(null);
      setCostValues({});
      setPlatformIds([]);
      setWrapperTypeIds([]);
      setSustainability({});
      setClientPreferenceValues({});
      setAllocationMode("none");
      setSelectedAssetClassId("");
      setAllocationWeights({});
    }
    autoAssignDoneRef.current = false;
    defaultProductTypeSeededRef.current = false;
    autoFilledIdentityRef.current = null;
    setError(null);
    setPatchError(null);
    setActiveTab(defaultTab);
  }, [product, open, localize, defaultTab]);

  // Auto-assign all cost types on new-product creation.
  // Fires exactly once per dialog mount:
  //   - If no ticker/dataset → fire immediately with calc=0, ovr=0 for all cost types.
  //   - If ticker/dataset present → wait until metadata fetch settles (success or error),
  //     then compute apiValues and fill: calc=apiValue|0, ovr=null when calc>0, ovr=0 otherwise.
  useEffect(() => {
    if (!open || product || autoAssignDoneRef.current) return;
    if (costTypes.length === 0) return;
    const hasTickerDataset = !!(formData.ticker && formData.dataset);
    if (hasTickerDataset && mainMetadataLoading) return;

    const computed = computeApiCostValues(
      hasTickerDataset ? (mainMetadata as Record<string, unknown> | null) : null,
      costTypes,
      localize,
    );
    const nextCostValues: Record<
      string,
      { calculatedValue: number; overriddenValue: number | null }
    > = {};
    for (const costType of costTypes) {
      const apiValue = computed.find((v) => v.costTypeId === costType.id);
      if (apiValue?.value !== null && apiValue?.value !== undefined) {
        nextCostValues[costType.id] = {
          calculatedValue: apiValue.value,
          overriddenValue: null,
        };
      } else {
        nextCostValues[costType.id] = { calculatedValue: 0, overriddenValue: 0 };
      }
    }
    setCostValues(nextCostValues);
    autoAssignDoneRef.current = true;
  }, [
    open,
    product,
    costTypes,
    formData.ticker,
    formData.dataset,
    mainMetadata,
    mainMetadataLoading,
    localize,
  ]);

  // QL-11 — pre-select the collection's default product type, once per
  // create-mode open. A separate effect rather than part of the reset above:
  // the suitability options query is gated on the dialog being open, so it
  // resolves *after* the reset has already written an empty value — a seed
  // folded into the reset would silently never fire.
  useEffect(() => {
    if (!open || product || defaultProductTypeSeededRef.current) return;
    // QL-12 — the query is disabled while the dialog is closed, so a toggle of
    // the collection default only *marks* it stale; on reopen React Query hands
    // back the previous payload and refetches behind it. Seeding from that would
    // lock in the old default, because this effect disarms itself. Stay armed
    // until the response has settled.
    if (suitabilityOptionsFetching) return;
    // Either the options have not arrived yet or the collection has no active
    // types. Nothing to seed; stay armed.
    if (suitabilityOptions.productTypes.length === 0) return;
    defaultProductTypeSeededRef.current = true;
    const defaultType = suitabilityOptions.productTypes.find((t) => t.isDefault);
    if (!defaultType) return;
    setFormData((prev) => (prev.productTypeId ? prev : { ...prev, productTypeId: defaultType.id }));
  }, [open, product, suitabilityOptions.productTypes, suitabilityOptionsFetching]);

  // QL-271 — the server refuses a patch whose NATIVE currency differs from the
  // instrument's, and that is much the most common rejection. Both currencies are
  // already in the form, so say so where the patch is picked rather than after
  // submitting. The server stays authoritative: this never blocks the save.
  // Mirrors the server's instrument_history_sufficient rule: an instrument whose
  // own series already reaches back PATCH_MAX_INSTRUMENT_HISTORY_YEARS is not
  // offered a patch. Unknown first date is "cannot judge", not "long enough".
  const patchNotNeeded = Boolean(
    formData.firstDate &&
      !isWithinLastNYears(formData.firstDate, PATCH_MAX_INSTRUMENT_HISTORY_YEARS),
  );

  const patchCurrencyMismatch = Boolean(
    formData.patchTicker &&
      formData.instrumentCurrency &&
      formData.patchCurrency &&
      formData.instrumentCurrency !== formData.patchCurrency,
  );

  // QL-171 — the patch extends the instrument's series backwards, so the joined
  // series runs from the earlier of the two starts to the instrument's own end.
  const combinedRange =
    linkedInstrument?.patchInstrument?.ticker &&
    formData.firstDate &&
    formData.lastDate &&
    formData.patchFirstDate
      ? {
          from:
            formData.patchFirstDate < formData.firstDate
              ? formData.patchFirstDate
              : formData.firstDate,
          to: formData.lastDate,
        }
      : null;

  const handleInstrumentSelect = (result: InstrumentSelectResult) => {
    const { instrument, dataset, metadata, error: metadataError } = result;
    setError(null);
    setPatchError(null);

    // If metadata fetch failed, show error but allow saving with search data
    if (metadataError || !metadata) {
      if (metadataError) {
        setError(`${metadataError} ${t.notLinkedWarning}`);
      }
      const name = instrument.name;
      const isin = instrument.isin || "";
      autoFilledIdentityRef.current = { name, isin };
      setFormData({
        ...formData,
        name,
        isin,
        ticker: metadataError ? "" : instrument.ticker, // Don't link if error
        dataset: metadataError ? "" : dataset, // Don't link if error
        firstDate: "",
        lastDate: "",
        instrumentCurrency: "",
      });
      setLinkedInstrument(
        metadataError
          ? null
          : {
              ticker: instrument.ticker,
              dataset: dataset,
              name: instrument.name,
            },
      );
    } else {
      // Use metadata from API (instrument was either found or added automatically)
      const name = metadata.name || instrument.name;
      const isin = metadata.isin || instrument.isin || "";
      autoFilledIdentityRef.current = { name, isin };
      setFormData({
        ...formData,
        name,
        isin,
        ticker: instrument.ticker,
        firstDate: metadata.firstdate || "",
        lastDate: metadata.lastdate || "",
        instrumentCurrency: metadata.currency || "",
        dataset: dataset,
      });

      setLinkedInstrument({
        ticker: instrument.ticker,
        dataset: dataset,
        name: metadata.name || instrument.name,
      });
    }
  };

  // Patch instrument will only be possible if there is already a linked instrument
  const handleSelectPatchInstrument = (result: InstrumentSelectResult) => {
    const { instrument, dataset, metadata, error: metadataError } = result;
    if (!instrument.ticker || !dataset || !linkedInstrument) return;

    setError(null);
    setPatchError(null);

    // If metadata fetch failed, show error and don't link
    if (metadataError || !metadata) {
      if (metadataError) {
        setError(metadataError);
        return;
      }
      // No metadata but no error - use search data directly
      setLinkedInstrument({
        ...linkedInstrument,
        patchInstrument: {
          ticker: instrument.ticker,
          dataset: dataset,
          name: instrument.name,
        },
      });
      setFormData((prev) => ({
        ...prev,
        patchTicker: instrument.ticker,
        patchDataset: dataset,
        patchFirstDate: "",
        patchLastDate: "",
        patchCurrency: "",
      }));
    } else {
      // Use metadata from API (for all dataset types including custom)
      setLinkedInstrument({
        ...linkedInstrument,
        patchInstrument: {
          ticker: instrument.ticker,
          dataset: dataset,
          name: metadata.name || instrument.name,
        },
      });
      setFormData((prev) => ({
        ...prev,
        patchTicker: instrument.ticker,
        patchDataset: dataset,
        patchFirstDate: metadata.firstdate || "",
        patchLastDate: metadata.lastdate || "",
        patchCurrency: metadata.currency || "",
      }));
    }
  };

  const handleRemoveInstrument = () => {
    setLinkedInstrument(null);
    setPatchError(null);
    // QL-174 — name/ISIN are cleared only while they still hold exactly the
    // instrument's values; an edited one is kept. The baseline is what a link
    // in this dialog session wrote, else the linked instrument's current
    // details — read here, not copied by an effect, so cached metadata on
    // reopen works. Never the saved product name. Not loaded, failed, or no
    // name/ISIN → that field is kept. A cleared name takes nameI18n with it
    // wholesale, or stale locales resurface on save. The required name input
    // blocks saving an empty name.
    const autoFilled =
      autoFilledIdentityRef.current ??
      (mainMetadata ? { name: mainMetadata.name, isin: mainMetadata.isin } : null);
    autoFilledIdentityRef.current = null;
    setFormData((prev) => {
      const clear = getIdentityFieldsToClear(prev, autoFilled);
      return {
        ...prev,
        ...(clear.name ? { name: "", nameI18n: {} } : {}),
        ...(clear.isin ? { isin: "" } : {}),
        ticker: "",
        dataset: "",
        patchTicker: "",
        patchDataset: "",
        instrumentCurrency: "",
        firstDate: "",
        lastDate: "",
        patchFirstDate: "",
        patchLastDate: "",
        patchCurrency: "",
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setPatchError(null);
    setLoading(true);

    try {
      const validCostValues: Record<
        string,
        { calculatedValue: number; overriddenValue: number | null }
      > = {};
      for (const [costTypeId, v] of Object.entries(costValues)) {
        if (!v) continue;
        if (v.overriddenValue !== null && !(v.overriddenValue >= 0)) {
          setError(t.overrideMustBePositive);
          setActiveTab("costs");
          setLoading(false);
          return;
        }
        validCostValues[costTypeId] = v;
      }

      // Minimum buy amount only applies to amount-ordered products — always null for "units".
      let minBuyAmount: number | null = null;
      if (formData.orderAmountType !== "units" && formData.minBuyAmount.trim() !== "") {
        const m = Number(formData.minBuyAmount.trim());
        if (!Number.isFinite(m) || m < 0) {
          setError(t.minBuyAmountInvalid);
          setActiveTab("suitability");
          setLoading(false);
          return;
        }
        minBuyAmount = m;
      }

      // QP-164 — calculation inputs (only meaningful in Products mode, but
      // accepted regardless — the field is simply hidden otherwise). Empty =
      // not entered (null). Volatility can never be negative.
      let expectedReturn: number | null = null;
      if (formData.expectedReturn.trim() !== "") {
        const r = Number(formData.expectedReturn.trim());
        if (!Number.isFinite(r)) {
          setError(t.expectedReturnInvalid);
          setActiveTab("calculationInput");
          setLoading(false);
          return;
        }
        expectedReturn = r;
      }

      let expectedVolatility: number | null = null;
      if (formData.expectedVolatility.trim() !== "") {
        const v = Number(formData.expectedVolatility.trim());
        if (!Number.isFinite(v) || v < 0) {
          setError(t.expectedVolatilityInvalid);
          setActiveTab("calculationInput");
          setLoading(false);
          return;
        }
        expectedVolatility = v;
      }

      // QL-10 — optional minimum risk score; empty = not configured (null).
      // The picker only offers valid scores, so there is nothing to validate
      // here; the server remains authoritative.
      const minRiskScore =
        formData.minRiskScore.trim() !== "" ? Number(formData.minRiskScore.trim()) : null;

      // Build asset allocations
      let assetAllocations: Array<{ assetClassId: string; weight: number }> = [];
      if (allocationMode === "single" && selectedAssetClassId) {
        assetAllocations = [{ assetClassId: selectedAssetClassId, weight: 100 }];
      } else if (allocationMode === "multiple") {
        assetAllocations = Object.entries(allocationWeights)
          .map(([assetClassId, weight]) => ({ assetClassId, weight: parseFloat(weight) || 0 }))
          .filter(({ weight }) => weight > 0);

        // Validate that weights sum to 100
        const total = assetAllocations.reduce((sum, a) => sum + a.weight, 0);
        if (assetAllocations.length > 0 && Math.abs(total - 100) > 0.01) {
          setError(`${t.allocationMustSum} (${total.toFixed(1)}%)`);
          setLoading(false);
          return;
        }
      }

      // Build the nameI18n with user's language as the primary value
      const nameI18n: LocalizedString = {
        ...formData.nameI18n,
        [userLanguage]: formData.name,
      };

      // The HTML5 `required` on the name input only guards while the General
      // tab is mounted — Radix unmounts inactive tabs, so submitting from
      // another tab bypasses it. The server enforces the same rule.
      if (Object.values(nameI18n).filter((v) => typeof v === "string" && v.trim()).length === 0) {
        setError(t.nameRequired);
        setActiveTab("general");
        setLoading(false);
        return;
      }

      // Build the descriptionI18n with user's language as the primary value
      const descriptionI18n: LocalizedString | undefined = formData.description
        ? {
            ...formData.descriptionI18n,
            [userLanguage]: formData.description,
          }
        : undefined;

      const submitData = {
        nameI18n,
        descriptionI18n,
        isin: formData.isin,
        ticker: formData.ticker,
        dataset: formData.dataset,
        patchTicker: formData.patchTicker,
        patchDataset: formData.patchDataset,
        firstDate: formData.firstDate,
        lastDate: formData.lastDate,
        productTypeId: formData.productTypeId,
        productCostClassId: formData.productCostClassId,
        minTimeHorizonId: formData.minTimeHorizonId,
        minRiskLevelId: formData.minRiskLevelId,
        // QL-10 — null clears the minimum risk score (0 is a real value).
        minRiskScore,
        // QP-195 — platform availability (empty = all platforms).
        platformIds,
        // Preference-ordered wrapper types. Omitted when the feature is off or
        // the collection has none configured (undefined = untouched).
        ...(wrapperTypesFeatureEnabled && wrapperTypes.length > 0 ? { wrapperTypeIds } : {}),
        ...(sustainabilityConfig ? { sustainability } : {}), // QP-179
        // QP-224 — full map incl. false entries so untagging persists (the
        // server keeps rows only for true values). Omitted when the collection
        // defines no preferences (undefined = untouched). QL-584 — a units
        // product carries none (the server refuses a tag), so a switch turned
        // on before the order type was changed is not sent.
        ...(clientPreferences.length > 0
          ? {
              clientPreferences: formData.orderAmountType === "units" ? {} : clientPreferenceValues,
            }
          : {}),
        minBuyAmount,
        expectedReturn,
        expectedVolatility,
        // Only send on create — immutable after creation (server also guards).
        ...(product ? {} : { orderAmountType: formData.orderAmountType }),
        costValues: validCostValues,
        assetAllocations,
      };

      const callbacks = {
        onSuccess: () => {
          setLoading(false);
          onClose();
        },
        onError: (err: Error) => {
          setError(err.message || t.somethingWentWrong);
          // QL-271 — a refused patch names the patch instrument in its payload;
          // pin the reason to the patch block as well as the banner.
          const body =
            err instanceof ApiError ? (err.body as { patchInstrumentId?: unknown }) : null;
          if (body?.patchInstrumentId) {
            setPatchError(err.message);
            setActiveTab("general");
          }
          setLoading(false);
        },
      };

      if (product) {
        updateProduct.mutate(
          { collectionId: portfolioCollectionId, productId: product.id, data: submitData },
          callbacks,
        );
      } else {
        createProduct.mutate({ collectionId: portfolioCollectionId, data: submitData }, callbacks);
      }
      return; // loading state managed by mutation callbacks
    } catch {
      setError(t.failedToSave);
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={() => onClose()}>
        <DialogScrollContent className="max-w-4xl">
          <DialogScrollHeader>
            <DialogTitle>
              {product
                ? editingProductName
                  ? `${t.editProduct} (${editingProductName})`
                  : t.editProduct
                : t.addNewProduct}
            </DialogTitle>
            <DialogDescription>
              {product ? t.editDescription : t.createDescription}
            </DialogDescription>
          </DialogScrollHeader>

          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="flex min-h-0 flex-1 flex-col"
            >
              {/* Pinned strip: error + tab bar stay visible while the body scrolls. */}
              <div className="px-6 pt-4">
                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md mb-4">
                    {error}
                  </div>
                )}
                {/* Documents tab is edit-only (requires a saved product id to
                  attach uploads against). */}
                <TabsList className="flex w-full justify-start gap-1">
                  <TabsTrigger value="general">{t.tabGeneral}</TabsTrigger>
                  <TabsTrigger value="suitability">{t.tabSuitability}</TabsTrigger>
                  {sustainabilityConfig && (
                    <TabsTrigger value="sustainability">{t.sustainabilitySection}</TabsTrigger>
                  )}
                  {clientPreferences.length > 0 && (
                    <TabsTrigger value="clientPreferences">{t.tabClientPreferences}</TabsTrigger>
                  )}
                  {costTypes.length > 0 && <TabsTrigger value="costs">{t.tabCosts}</TabsTrigger>}
                  {assetClasses.length > 0 && (
                    <TabsTrigger value="allocation">{t.tabAllocation}</TabsTrigger>
                  )}
                  {isProductsMode && (
                    <TabsTrigger value="calculationInput">{t.tabCalculationInput}</TabsTrigger>
                  )}
                  {product && <TabsTrigger value="documents">{t.tabDocuments}</TabsTrigger>}
                </TabsList>
              </div>

              <DialogBody>
                <TabsContent value="general" className="space-y-4 py-4">
                  {/* Instrument Search Section */}
                  <div className="space-y-2">
                    <Label>{t.linkToInstrument}</Label>
                    {linkedInstrument ? (
                      <div className="flex flex-col gap-4">
                        <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                          <div className="flex-1">
                            <p className="text-sm font-medium">{linkedInstrument.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {linkedInstrument.ticker} ({linkedInstrument.dataset})
                            </p>
                            {formData.firstDate && formData.lastDate ? (
                              <p className="text-xs text-muted-foreground">
                                {t.instrumentDuration}: {formatDate(formData.firstDate)} -{" "}
                                {formatDate(formData.lastDate)}
                              </p>
                            ) : null}
                            {formData.instrumentCurrency ? (
                              <p className="text-xs text-muted-foreground">
                                {t.instrumentCurrency}: {formData.instrumentCurrency}
                              </p>
                            ) : null}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            tooltip={t.removeInstrument}
                            onClick={handleRemoveInstrument}
                            disabled={loading || isPending}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        {/* QL-271 — a patch that EXISTS is always shown, whatever the
                          instrument's history length: one set by the AI tool or saved
                          before validation existed must be visible and clearable here.
                          The ten-year rule only governs OFFERING a new patch, below. */}
                        {linkedInstrument.patchInstrument?.ticker ? (
                          <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                            <div className="flex-1">
                              <p className="text-xs text-muted-foreground">{t.patchInstrument}</p>
                              <p className="text-sm font-medium">
                                {linkedInstrument.patchInstrument.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {linkedInstrument.patchInstrument.ticker} (
                                {linkedInstrument.patchInstrument.dataset})
                              </p>
                              {formData.patchFirstDate && formData.patchLastDate ? (
                                <p className="text-xs text-muted-foreground">
                                  {t.instrumentDuration}: {formatDate(formData.patchFirstDate)} -{" "}
                                  {formatDate(formData.patchLastDate)}
                                </p>
                              ) : null}
                              {formData.patchCurrency ? (
                                <p className="text-xs text-muted-foreground">
                                  {t.instrumentCurrency}: {formData.patchCurrency}
                                </p>
                              ) : null}
                              {patchCurrencyMismatch && !patchError ? (
                                <p className="text-xs text-warning">
                                  {t.patchCurrencyMismatch(
                                    formData.patchCurrency,
                                    formData.instrumentCurrency,
                                  )}
                                </p>
                              ) : null}
                              {patchError ? (
                                <p className="text-xs text-destructive">{patchError}</p>
                              ) : null}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              tooltip={t.removeInstrument}
                              onClick={() => {
                                setLinkedInstrument({
                                  ...linkedInstrument,
                                  patchInstrument: undefined,
                                });
                                setPatchError(null);
                                setFormData((prev) => ({
                                  ...prev,
                                  patchTicker: "",
                                  patchDataset: "",
                                  patchFirstDate: "",
                                  patchLastDate: "",
                                  patchCurrency: "",
                                }));
                              }}
                              disabled={loading || isPending}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : patchNotNeeded ? (
                          // Always offered, disabled with the reason when the
                          // instrument's own history is long enough — the server
                          // refuses such a patch (instrument_history_sufficient),
                          // so say so here instead of hiding the button. The span
                          // carries the hover: a disabled button has
                          // pointer-events-none, so its own tooltip cannot.
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex w-full">
                                <Button
                                  type="button"
                                  variant="outline"
                                  disabled
                                  className="w-full"
                                  aria-describedby="patch-not-needed"
                                >
                                  {t.patchInstrument}
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent id="patch-not-needed" className="max-w-xs">
                              {t.patchNotNeeded(String(PATCH_MAX_INSTRUMENT_HISTORY_YEARS))}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setPatchInstrumentDialogOpen(true)}
                            // Unknown first date (metadata still loading): hold
                            // the button rather than offer a patch that may be
                            // refused.
                            disabled={loading || isPending || !formData.firstDate}
                            className="w-full"
                          >
                            {t.patchInstrument}
                          </Button>
                        )}
                        {combinedRange ? (
                          <p className="text-xs text-muted-foreground">
                            {t.combinedDuration}: {formatDate(combinedRange.from)} -{" "}
                            {formatDate(combinedRange.to)}
                          </p>
                        ) : null}
                      </div>
                    ) : (
                      <Button
                        data-dialog-guard-ignore
                        type="button"
                        variant="outline"
                        onClick={() => setSearchDialogOpen(true)}
                        disabled={loading || isPending}
                        className="w-full"
                      >
                        {t.searchInstrumentDatabase}
                      </Button>
                    )}
                    <p className="text-xs text-muted-foreground">{t.linkDescription}</p>
                  </div>

                  <LocalizedInput
                    id="name"
                    label={t.productName}
                    value={formData.name}
                    i18nValue={formData.nameI18n}
                    onChange={(value) => setFormData({ ...formData, name: value })}
                    onI18nChange={(i18nValue) => setFormData({ ...formData, nameI18n: i18nValue })}
                    supportedLanguages={tenantSupportedLanguages}
                    placeholder={t.productNamePlaceholder}
                    required
                    disabled={isPending}
                  />

                  <div className="space-y-2">
                    <Label htmlFor="isin">{t.isinOptional}</Label>
                    <Input
                      id="isin"
                      value={formData.isin}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          isin: e.target.value.toUpperCase(),
                        })
                      }
                      placeholder={t.isinPlaceholder}
                      maxLength={12}
                      disabled={isPending}
                    />
                    <p className="text-xs text-muted-foreground">{t.isinDescription}</p>
                  </div>

                  <LocalizedInput
                    id="description"
                    label={t.descriptionOptional}
                    value={formData.description}
                    i18nValue={formData.descriptionI18n}
                    onChange={(value) => setFormData({ ...formData, description: value })}
                    onI18nChange={(i18nValue) =>
                      setFormData({ ...formData, descriptionI18n: i18nValue })
                    }
                    supportedLanguages={tenantSupportedLanguages}
                    placeholder={t.descriptionPlaceholder}
                    disabled={isPending}
                    multiline
                    rows={3}
                  />

                  {/* Order type: amount vs units (immutable after creation). */}
                  <div className="space-y-2">
                    <Label htmlFor="orderType">{t.orderType}</Label>
                    <Select
                      value={formData.orderAmountType}
                      onValueChange={(v) =>
                        setFormData({ ...formData, orderAmountType: v as "amount" | "units" })
                      }
                      disabled={isPending || !!product}
                    >
                      <SelectTrigger id="orderType">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="amount">{t.orderTypeAmount}</SelectItem>
                        <SelectItem value="units" disabled={!product && !orderUnitsLastPrice}>
                          {t.orderTypeUnits}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {orderUnitsLastPrice ? (
                      <p className="text-xs text-muted-foreground">
                        {t.lastPriceLabel}: {orderUnitsLastPrice.value}
                        {orderUnitsLastPrice.currency ? ` ${orderUnitsLastPrice.currency}` : ""}
                        {orderUnitsLastPrice.date ? ` (${orderUnitsLastPrice.date})` : ""}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">{t.unitsNeedLastPrice}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {product ? t.orderTypeImmutable : t.orderTypeDescription}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="productCostClassId">{t.costClass}</Label>
                    <Select
                      value={formData.productCostClassId || "__none__"}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          productCostClassId: value === "__none__" ? "" : value,
                        })
                      }
                      disabled={isPending || activeCostClasses.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            activeCostClasses.length === 0
                              ? t.noCostClassesConfigured
                              : t.selectCostClass
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t.none}</SelectItem>
                        {activeCostClasses.map((cls) => (
                          <SelectItem key={cls.id} value={cls.id}>
                            {localize(cls.nameI18n)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">{t.costClassDescription}</p>
                  </div>
                </TabsContent>

                <TabsContent value="suitability" className="space-y-4 py-4">
                  <div className="text-sm text-muted-foreground mb-4">
                    {t.suitabilityDescription}
                  </div>

                  {productTypesFeatureEnabled && (
                    <div className="space-y-2">
                      <Label htmlFor="productTypeId">{t.productType}</Label>
                      <Select
                        value={formData.productTypeId}
                        onValueChange={(value) => {
                          // QL-11 — a deliberate choice, including clearing to
                          // "None", must survive a late-arriving options response.
                          defaultProductTypeSeededRef.current = true;
                          setFormData({
                            ...formData,
                            productTypeId: value === "__none__" ? "" : value,
                          });
                        }}
                        disabled={isPending || suitabilityOptions.productTypes.length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              suitabilityOptions.productTypes.length === 0
                                ? t.noProductTypesConfigured
                                : t.selectProductType
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">{t.none}</SelectItem>
                          {suitabilityOptions.productTypes.map((type) => (
                            <SelectItem key={type.id} value={type.id}>
                              {localize(type.nameI18n)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">{t.productTypeDescription}</p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="minTimeHorizonId">{t.minTimeHorizon}</Label>
                    <Select
                      value={formData.minTimeHorizonId}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          minTimeHorizonId: value === "__none__" ? "" : value,
                        })
                      }
                      disabled={isPending || suitabilityOptions.timeHorizons.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            suitabilityOptions.timeHorizons.length === 0
                              ? t.noTimeHorizonsConfigured
                              : t.selectMinTimeHorizon
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t.none}</SelectItem>
                        {suitabilityOptions.timeHorizons.map((th) => (
                          <SelectItem key={th.id} value={th.id}>
                            {localize(th.labelI18n)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">{t.minTimeHorizonDescription}</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="minRiskLevelId">{t.minRiskTolerance}</Label>
                    <Select
                      value={formData.minRiskLevelId}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          minRiskLevelId: value === "__none__" ? "" : value,
                        })
                      }
                      disabled={isPending || suitabilityOptions.riskLevels.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            suitabilityOptions.riskLevels.length === 0
                              ? t.noRiskLevelsConfigured
                              : t.selectMinRiskTolerance
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t.none}</SelectItem>
                        {suitabilityOptions.riskLevels.map((rl) => (
                          <SelectItem key={rl.id} value={rl.id}>
                            {localize(rl.labelI18n)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">{t.minRiskToleranceDescription}</p>
                  </div>

                  {/* QL-10 — minimum risk score, independent of the risk level above.
                    Disabled when the collection has no risk model / matrix scores. */}
                  <div className="space-y-2">
                    <Label htmlFor="minRiskScore">
                      {t.minRiskScore}{" "}
                      <span className="text-xs font-normal text-muted-foreground">
                        ({t.minBuyAmountOptional})
                      </span>
                    </Label>
                    <Select
                      value={formData.minRiskScore || "__none__"}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          minRiskScore: value === "__none__" ? "" : value,
                        })
                      }
                      disabled={isPending || minRiskScoreOptions.length === 0}
                    >
                      <SelectTrigger id="minRiskScore">
                        <SelectValue
                          placeholder={
                            minRiskScoreOptions.length === 0
                              ? t.minRiskScoreNoMatrix
                              : t.selectMinRiskScore
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t.none}</SelectItem>
                        {minRiskScoreOptions.map((score) => (
                          <SelectItem key={score} value={String(score)}>
                            {score}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {minRiskScoreOptions.length === 0
                        ? t.minRiskScoreNoMatrix
                        : t.minRiskScoreDescription}
                    </p>
                  </div>

                  {/* QP-195 — platform availability (only when the collection has platforms) */}
                  {platforms.length > 0 && (
                    <div className="space-y-2">
                      <Label>{t.platforms}</Label>
                      {/* QL-163 — the list runs long on collections with many
                          platforms, so it splits into two columns from `sm:` up.
                          `OptionRow`'s min-height is the row rhythm; the grid
                          only adds the column gutter. */}
                      <div className="grid gap-x-6 sm:grid-cols-2">
                        {platforms.map((platform) => (
                          <OptionRow key={platform.id} className="text-sm font-normal">
                            <Checkbox
                              id={`product-platform-${platform.id}`}
                              checked={platformIds.includes(platform.id)}
                              onCheckedChange={() =>
                                setPlatformIds((prev) =>
                                  prev.includes(platform.id)
                                    ? prev.filter((id) => id !== platform.id)
                                    : [...prev, platform.id],
                                )
                              }
                              disabled={isPending}
                            />
                            <Label
                              htmlFor={`product-platform-${platform.id}`}
                              className="font-normal cursor-pointer"
                            >
                              {localize(platform.labelI18n)}
                            </Label>
                          </OptionRow>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">{t.platformsDescription}</p>
                    </div>
                  )}

                  {/* Wrapper types (only when enabled on the collection and it has active ones) */}
                  {wrapperTypesFeatureEnabled && activeWrapperTypes.length > 0 && (
                    <div className="space-y-2">
                      <Label>{t.wrapperTypes}</Label>
                      {(() => {
                        const availableWrapperTypes = activeWrapperTypes.filter(
                          (wt) => !wrapperTypeIds.includes(wt.id),
                        );
                        if (availableWrapperTypes.length === 0) return null;
                        return (
                          <Select
                            value=""
                            onValueChange={(id) => {
                              if (id) setWrapperTypeIds((prev) => [...prev, id]);
                            }}
                            disabled={isPending}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={t.addWrapperType} />
                            </SelectTrigger>
                            <SelectContent>
                              {availableWrapperTypes.map((wt) => (
                                <SelectItem key={wt.id} value={wt.id}>
                                  {localize(wt.nameI18n)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        );
                      })()}
                      {wrapperTypeIds.length > 0 && (
                        <div className="flex flex-col gap-1 rounded-md border p-2">
                          {wrapperTypeIds.map((id, index) => {
                            const wt = wrapperTypes.find((w) => w.id === id);
                            if (!wt) return null;
                            const handleMove = (direction: -1 | 1) => {
                              const target = index + direction;
                              if (target < 0 || target >= wrapperTypeIds.length) return;
                              setWrapperTypeIds((prev) => {
                                const next = [...prev];
                                const [moved] = next.splice(index, 1);
                                next.splice(target, 0, moved!);
                                return next;
                              });
                            };
                            return (
                              <div key={id} className="flex items-center gap-2 text-sm">
                                <span className="w-5 text-muted-foreground">{index + 1}.</span>
                                <span className="flex-1">{localize(wt.nameI18n)}</span>
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="ghost"
                                  tooltip={t.moveUp}
                                  onClick={() => handleMove(-1)}
                                  disabled={isPending || index === 0}
                                >
                                  <ArrowUp className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="ghost"
                                  tooltip={t.moveDown}
                                  onClick={() => handleMove(1)}
                                  disabled={isPending || index === wrapperTypeIds.length - 1}
                                >
                                  <ArrowDown className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="ghost"
                                  tooltip={t.removeWrapperType}
                                  onClick={() =>
                                    setWrapperTypeIds((prev) => prev.filter((x) => x !== id))
                                  }
                                  disabled={isPending}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground">{t.wrapperTypesDescription}</p>
                    </div>
                  )}

                  {/* Minimum buy amount does not apply to unit-ordered products. */}
                  {formData.orderAmountType !== "units" && (
                    <div className="space-y-2">
                      <Label htmlFor="minBuyAmount">
                        {t.minBuyAmount}{" "}
                        <span className="text-xs font-normal text-muted-foreground">
                          ({t.minBuyAmountOptional})
                        </span>
                      </Label>
                      <Input
                        id="minBuyAmount"
                        type="number"
                        step="any"
                        min="0"
                        value={formData.minBuyAmount}
                        onChange={(e) => setFormData({ ...formData, minBuyAmount: e.target.value })}
                        placeholder={t.minBuyAmountPlaceholder}
                        disabled={isPending}
                        className="w-32"
                      />
                      <p className="text-xs text-muted-foreground">{t.minBuyAmountDescription}</p>
                    </div>
                  )}
                </TabsContent>

                {/* QP-179 — sustainability values (own tab, only when the collection has a config) */}
                {sustainabilityConfig && (
                  <TabsContent value="sustainability" className="space-y-4 py-4">
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">{t.sustainabilityDescription}</p>

                      {sustainabilityConfig.sfdr &&
                        (() => {
                          const sfdrConfig = sustainabilityConfig.sfdr;
                          const scoreLabel = (value: SfdrScoreValue): string => {
                            const s = (sfdrConfig.scores ?? []).find((sc) => sc.value === value);
                            return s ? localize(s.labelI18n) || String(value) : String(value);
                          };
                          const override = sustainability.sfdrOverride ?? null;
                          const setOverride = (value: SfdrScoreValue | null) =>
                            setSustainability((prev) => {
                              const { sfdrOverride: _o, ...rest } = prev;
                              return value == null ? rest : { ...rest, sfdrOverride: value };
                            });
                          return (
                            <div className="space-y-1">
                              <Label htmlFor="sustainability-sfdr">{t.sfdrShort}</Label>
                              {instrumentSfdr != null ? (
                                <div className="space-y-1">
                                  <p className="text-sm">
                                    {t.sfdrFromMorningstar}{" "}
                                    <span className="font-medium">
                                      {scoreLabel(instrumentSfdr)}
                                    </span>
                                  </p>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground">
                                      {t.sfdrOverride}
                                    </span>
                                    <Select
                                      value={override != null ? String(override) : "__auto__"}
                                      onValueChange={(v) =>
                                        setOverride(
                                          v === "__auto__" ? null : (Number(v) as SfdrScoreValue),
                                        )
                                      }
                                      disabled={isPending}
                                    >
                                      <SelectTrigger id="sustainability-sfdr" className="w-56">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="__auto__">
                                          {t.sfdrUseMorningstar}
                                        </SelectItem>
                                        {SFDR_SCORE_VALUES.map((value) => (
                                          <SelectItem key={value} value={String(value)}>
                                            {scoreLabel(value)}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-1">
                                  <Select
                                    value={override != null ? String(override) : ""}
                                    onValueChange={(v) =>
                                      setOverride(v === "" ? null : (Number(v) as SfdrScoreValue))
                                    }
                                    disabled={isPending}
                                  >
                                    <SelectTrigger id="sustainability-sfdr" className="w-56">
                                      <SelectValue placeholder={t.sfdrSelectScore} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {SFDR_SCORE_VALUES.map((value) => (
                                        <SelectItem key={value} value={String(value)}>
                                          {scoreLabel(value)}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <p className="text-xs text-muted-foreground">
                                    {t.sfdrManualHint}
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                      {(sustainabilityConfig.bars ?? []).map((bar) => (
                        <div key={bar.key} className="space-y-2">
                          <Label htmlFor={`sustainability-bar-${bar.key}`}>
                            {localize(bar.labelI18n) || bar.key}
                          </Label>
                          <Select
                            value={sustainability.bars?.[bar.key] ?? ""}
                            onValueChange={(v) =>
                              setSustainability((prev) => {
                                const bars = { ...(prev.bars ?? {}) };
                                if (v === "") delete bars[bar.key];
                                else bars[bar.key] = v;
                                return { ...prev, bars };
                              })
                            }
                            disabled={isPending}
                          >
                            <SelectTrigger id={`sustainability-bar-${bar.key}`} className="w-56">
                              <SelectValue placeholder={t.sustainabilitySelectStep} />
                            </SelectTrigger>
                            <SelectContent>
                              {(bar.steps ?? []).map((step) => (
                                <SelectItem key={step.key} value={step.key}>
                                  {localize(step.labelI18n) || step.key}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}

                      {(sustainabilityConfig.productPreferences ?? []).length > 0 && (
                        <div className="flex flex-col gap-3">
                          {(sustainabilityConfig.productPreferences ?? []).map((pref) => {
                            const label = localize(pref.labelI18n) || pref.key;
                            const override = sustainability.productPreferences?.[pref.key];
                            const setProductPreferenceValue = (value: boolean | null) =>
                              setSustainability((prev) => {
                                const productPreferences = { ...(prev.productPreferences ?? {}) };
                                if (value === null) delete productPreferences[pref.key];
                                else productPreferences[pref.key] = value;
                                return { ...prev, productPreferences };
                              });
                            // Mapped product preference → live-resolve from the instrument's
                            // ESG maps per the preference's exclusionMode (default "either").
                            const prefMode =
                              pref.exclusionMode ?? DEFAULT_SUSTAINABILITY_EXCLUSION_MODE;
                            const instrumentValue = pref.exclusionKey
                              ? resolveProductPreferenceFromInstrument(
                                  pref.exclusionKey,
                                  prefMode,
                                  instrumentEsgMaps,
                                )
                              : undefined;
                            // Label the resolved value by the ACTUAL signal that
                            // decided it — so "Either" mode reads "Excluded" when the
                            // stated policy carried it, "Not involved"/"Involved" when
                            // the involvement signal did — rather than a fixed pair.
                            const exclSig = pref.exclusionKey
                              ? instrumentEsgMaps.esgExclusions?.[pref.exclusionKey]
                              : undefined;
                            const invSig = pref.exclusionKey
                              ? instrumentEsgMaps.esgInvolvement?.[pref.exclusionKey]
                              : undefined;
                            const resolvedLabel = (): string => {
                              if (prefMode === "excludes") {
                                return exclSig === true
                                  ? t.productPreferenceExcluded
                                  : t.productPreferenceNotExcluded;
                              }
                              if (prefMode === "not_involved") {
                                return invSig === false
                                  ? t.productPreferenceClean
                                  : t.productPreferenceNotClean;
                              }
                              // "either": show whichever signal made it clean/not.
                              if (exclSig === true) return t.productPreferenceExcluded;
                              if (invSig === false) return t.productPreferenceClean;
                              return t.productPreferenceNotClean;
                            };
                            if (pref.exclusionKey && typeof instrumentValue === "boolean") {
                              return (
                                <div key={pref.key} className="space-y-1">
                                  <div className="flex items-center gap-2 text-sm">
                                    {pref.iconSvg && (
                                      <InlineSvg
                                        svg={pref.iconSvg}
                                        className="[&_svg]:h-4 [&_svg]:w-4 text-muted-foreground"
                                      />
                                    )}
                                    <span className="font-medium">{label}</span>
                                    <span className="text-xs text-muted-foreground">
                                      {t.productPreferenceFromMorningstar}: {resolvedLabel()}
                                    </span>
                                  </div>
                                  <Select
                                    value={
                                      override === true
                                        ? "yes"
                                        : override === false
                                          ? "no"
                                          : "__auto__"
                                    }
                                    onValueChange={(v) =>
                                      setProductPreferenceValue(
                                        v === "__auto__" ? null : v === "yes",
                                      )
                                    }
                                    disabled={isPending}
                                  >
                                    <SelectTrigger className="w-56">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__auto__">
                                        {t.productPreferenceAuto}
                                      </SelectItem>
                                      <SelectItem value="yes">
                                        {t.productPreferenceOverridePositive}
                                      </SelectItem>
                                      <SelectItem value="no">
                                        {t.productPreferenceOverrideNegative}
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              );
                            }
                            // Unmapped product preference (or mapped but no instrument value)
                            // → manual boolean.
                            return (
                              <OptionRow key={pref.key} className="text-sm">
                                <Checkbox
                                  id={`sustainability-product-preference-${pref.key}`}
                                  checked={override === true}
                                  onCheckedChange={(checked) =>
                                    setProductPreferenceValue(checked === true)
                                  }
                                  disabled={isPending}
                                />
                                {pref.iconSvg && (
                                  <InlineSvg
                                    svg={pref.iconSvg}
                                    className="[&_svg]:h-4 [&_svg]:w-4 text-muted-foreground"
                                  />
                                )}
                                <Label
                                  htmlFor={`sustainability-product-preference-${pref.key}`}
                                  className="font-normal cursor-pointer"
                                >
                                  {label}
                                </Label>
                              </OptionRow>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </TabsContent>
                )}

                {clientPreferences.length > 0 && (
                  <TabsContent value="clientPreferences" className="py-4">
                    <div className="space-y-4">
                      <div className="text-sm text-muted-foreground">
                        {t.clientPreferencesDescription}
                      </div>
                      {formData.orderAmountType === "units" && (
                        <p className="text-sm text-muted-foreground">
                          {t.clientPreferencesUnitsNotAllowed}
                        </p>
                      )}
                      <div className="space-y-3">
                        {clientPreferences.map((pref) => (
                          <div key={pref.id} className="flex items-center gap-2 text-sm">
                            <Switch
                              id={`client-preference-${pref.id}`}
                              checked={
                                formData.orderAmountType !== "units" &&
                                clientPreferenceValues[pref.id] === true
                              }
                              onCheckedChange={(checked) =>
                                setClientPreferenceValues((prev) => ({
                                  ...prev,
                                  [pref.id]: checked === true,
                                }))
                              }
                              disabled={isPending || formData.orderAmountType === "units"}
                            />
                            <Label
                              htmlFor={`client-preference-${pref.id}`}
                              className="font-normal cursor-pointer"
                            >
                              {localize(pref.nameI18n)}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </TabsContent>
                )}

                {costTypes.length > 0 && (
                  <TabsContent value="costs" className="py-4">
                    <ProductCostsDisplay
                      ticker={formData.ticker || null}
                      dataset={formData.dataset || null}
                      costTypes={costTypes}
                      costValues={costValues}
                      onCostValuesChange={setCostValues}
                      onNavigateToGeneral={() => setActiveTab("general")}
                      portfolioCollectionId={portfolioCollectionId}
                    />
                  </TabsContent>
                )}

                {assetClasses.length > 0 && (
                  <TabsContent value="allocation" className="py-4">
                    <div className="space-y-4">
                      <div className="text-sm text-muted-foreground">{t.allocationDescription}</div>

                      {allocationMode === "none" ? (
                        <div className="text-center py-6 border rounded-md bg-muted/30">
                          <p className="text-sm text-muted-foreground mb-4">{t.noAllocationSet}</p>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setAllocationMode("single");
                              if (assetClasses.length > 0) {
                                setSelectedAssetClassId(assetClasses[0].id);
                              }
                            }}
                          >
                            {t.addAllocation}
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="space-y-2">
                            <Label>{t.allocationMode}</Label>
                            <RadioGroup
                              value={allocationMode}
                              onValueChange={(value: "single" | "multiple") => {
                                if (value === "single") {
                                  // Switching to single: pick the first/highest weighted class
                                  const entries = Object.entries(allocationWeights);
                                  if (entries.length > 0) {
                                    // Pick the one with highest weight
                                    const sorted = entries.sort(
                                      (a, b) => parseFloat(b[1]) - parseFloat(a[1]),
                                    );
                                    setSelectedAssetClassId(sorted[0][0]);
                                  } else if (!selectedAssetClassId && assetClasses.length > 0) {
                                    setSelectedAssetClassId(assetClasses[0].id);
                                  }
                                  setAllocationWeights({});
                                } else {
                                  // Switching to multiple: keep the single selection as 100%
                                  if (selectedAssetClassId) {
                                    setAllocationWeights({ [selectedAssetClassId]: "100" });
                                  }
                                  setSelectedAssetClassId("");
                                }
                                setAllocationMode(value);
                              }}
                              className="flex gap-4"
                            >
                              <OptionRow>
                                <RadioGroupItem value="single" id="single" />
                                <Label htmlFor="single" className="font-normal cursor-pointer">
                                  {t.single100}
                                </Label>
                              </OptionRow>
                              <OptionRow>
                                <RadioGroupItem value="multiple" id="multiple" />
                                <Label htmlFor="multiple" className="font-normal cursor-pointer">
                                  {t.multiple}
                                </Label>
                              </OptionRow>
                            </RadioGroup>
                          </div>

                          {allocationMode === "single" && (
                            <div className="space-y-2">
                              <Label>{t.assetClass}</Label>
                              <Select
                                value={selectedAssetClassId}
                                onValueChange={setSelectedAssetClassId}
                                disabled={isPending}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder={t.selectAssetClass} />
                                </SelectTrigger>
                                <SelectContent>
                                  {assetClasses.map((ac) => (
                                    <SelectItem key={ac.id} value={ac.id}>
                                      {localize(ac.nameI18n)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-muted-foreground">{t.allocated100}</p>
                            </div>
                          )}

                          {allocationMode === "multiple" && (
                            <div className="space-y-3">
                              {/* List of added allocations */}
                              {Object.entries(allocationWeights).map(([assetClassId, weight]) => {
                                const ac = assetClasses.find((a) => a.id === assetClassId);
                                if (!ac) return null;
                                return (
                                  <div key={assetClassId} className="flex items-center gap-3">
                                    <span className="flex-1 text-sm">{localize(ac.nameI18n)}</span>
                                    <div className="flex items-center gap-1">
                                      <Input
                                        type="number"
                                        min="0.1"
                                        max="100"
                                        step="0.1"
                                        value={weight}
                                        onChange={(e) => {
                                          setAllocationWeights((prev) => ({
                                            ...prev,
                                            [assetClassId]: e.target.value,
                                          }));
                                        }}
                                        className="w-20 text-right"
                                        disabled={isPending}
                                      />
                                      <span className="text-sm text-muted-foreground">%</span>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      tooltip={t.removeAllocation}
                                      onClick={() => {
                                        setAllocationWeights((prev) => {
                                          const next = { ...prev };
                                          delete next[assetClassId];
                                          return next;
                                        });
                                      }}
                                      disabled={isPending}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                );
                              })}

                              {/* Add asset class dropdown */}
                              {(() => {
                                const usedIds = Object.keys(allocationWeights);
                                const availableClasses = assetClasses.filter(
                                  (ac) => !usedIds.includes(ac.id),
                                );
                                if (availableClasses.length === 0) return null;
                                return (
                                  <div className="flex items-center gap-2 pt-2">
                                    <Select
                                      value=""
                                      onValueChange={(assetClassId) => {
                                        if (assetClassId) {
                                          setAllocationWeights((prev) => ({
                                            ...prev,
                                            [assetClassId]: "",
                                          }));
                                        }
                                      }}
                                      disabled={isPending}
                                    >
                                      <SelectTrigger className="flex-1">
                                        <SelectValue placeholder={t.addAssetClass} />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {availableClasses.map((ac) => (
                                          <SelectItem key={ac.id} value={ac.id}>
                                            {localize(ac.nameI18n)}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                );
                              })()}

                              {/* Total */}
                              {Object.keys(allocationWeights).length > 0 && (
                                <div className="border-t pt-3 mt-3 flex items-center justify-between">
                                  <span className="text-sm font-medium">{t.total}</span>
                                  <span
                                    className={`text-sm font-medium ${
                                      Math.abs(
                                        Object.values(allocationWeights).reduce(
                                          (sum, w) => sum + (parseFloat(w) || 0),
                                          0,
                                        ) - 100,
                                      ) < 0.01
                                        ? "text-success"
                                        : "text-destructive"
                                    }`}
                                  >
                                    {Object.values(allocationWeights)
                                      .reduce((sum, w) => sum + (parseFloat(w) || 0), 0)
                                      .toFixed(1)}
                                    %
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {!productHasExistingAllocations && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => {
                                setAllocationMode("none");
                                setSelectedAssetClassId("");
                                setAllocationWeights({});
                              }}
                            >
                              {t.clearAllocation}
                            </Button>
                          )}
                          {productHasExistingAllocations && (
                            <p className="text-xs text-muted-foreground">
                              {t.allocationCannotBeRemoved}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </TabsContent>
                )}

                {isProductsMode && (
                  <TabsContent value="calculationInput" className="space-y-4 py-4">
                    <p className="text-xs text-muted-foreground">{t.calculationInputDescription}</p>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="expectedReturn">{t.expectedReturn}</Label>
                        <div className="flex items-center gap-1">
                          <Input
                            id="expectedReturn"
                            type="number"
                            step="any"
                            value={formData.expectedReturn}
                            onChange={(e) =>
                              setFormData({ ...formData, expectedReturn: e.target.value })
                            }
                            placeholder={t.expectedReturnPlaceholder}
                            disabled={isPending}
                            className="w-32"
                          />
                          <span className="text-muted-foreground">%</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="expectedVolatility">{t.expectedVolatility}</Label>
                        <div className="flex items-center gap-1">
                          <Input
                            id="expectedVolatility"
                            type="number"
                            step="any"
                            min="0"
                            value={formData.expectedVolatility}
                            onChange={(e) =>
                              setFormData({ ...formData, expectedVolatility: e.target.value })
                            }
                            placeholder={t.expectedVolatilityPlaceholder}
                            disabled={isPending}
                            className="w-32"
                          />
                          <span className="text-muted-foreground">%</span>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                )}

                {product && (
                  <TabsContent value="documents" className="py-4">
                    <ProductDocumentsTab
                      productId={product.id}
                      productIsin={formData.isin || null}
                      portfolioCollectionId={portfolioCollectionId}
                    />
                  </TabsContent>
                )}
              </DialogBody>
            </Tabs>

            <DialogScrollFooter>
              <DialogCancel disabled={loading || isPending}>{t.cancel}</DialogCancel>
              <Button type="submit" loading={loading || isPending}>
                {product ? t.save : t.addProduct}
              </Button>
            </DialogScrollFooter>
          </form>
        </DialogScrollContent>
      </Dialog>

      {/* Opening instrument search dialog in "Search instrument" mode */}
      <InstrumentSearchDialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
        onSelect={handleInstrumentSelect}
      />

      {/* Opening instrument search dialog in "Patch instrument" mode */}
      <InstrumentSearchDialog
        isPatchMode={true}
        open={patchInstrumentDialogOpen}
        linkedInstrument={linkedInstrument}
        onClose={() => setPatchInstrumentDialogOpen(false)}
        onSelect={handleSelectPatchInstrument}
      />
    </>
  );
}
